import styled, { keyframes } from 'styled-components';

export const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: ${(props) => props.width || '100%'};
  max-width: ${(props) => props.maxWidth || 'none'};
  min-width: 0;
  box-sizing: border-box;

  /* Prevent children from expanding beyond container */
  > label,
  > input,
  > div {
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.theme.name === 'dark' ? '#E5E7EB' : '#4B5563')};
`;

export const Required = styled.span`
  color: #ef4444;
`;

export const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

export const SearchInput = styled.input`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 12px 14px;
  padding-right: 40px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) => {
      if (props.error) return '#ef4444';
      if (props.isOpen) return props.theme?.colors?.primary || '#fa4616';
      return props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.15)';
    }};
  border-radius: 6px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  cursor: pointer;

  &::placeholder {
    color: ${(props) => (props.theme.name === 'dark' ? '#64748b' : '#9CA3AF')};
  }

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.02)'};
  }

  &:focus {
    border-color: ${(props) =>
      props.error ? '#ef4444' : props.theme?.colors?.primary || '#fa4616'};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.error ? 'rgba(239, 68, 68, 0.1)' : 'rgba(250, 70, 22, 0.1)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ChevronIcon = styled.span`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%)
    ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  pointer-events: none;
  color: ${(props) => (props.theme.name === 'dark' ? '#64748b' : '#9CA3AF')};
  transition: transform 0.15s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  margin-top: 4px;
  box-sizing: border-box;
  background: ${(props) =>
    props.theme.name === 'dark' ? '#1a1a2e' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 8px;
  max-height: 280px;
  overflow-y: auto;
  z-index: 100;
  box-shadow: ${(props) =>
    props.theme.name === 'dark'
      ? '0 4px 16px rgba(0, 0, 0, 0.5)'
      : '0 4px 16px rgba(0, 0, 0, 0.1)'};
`;

export const DropdownOption = styled.div`
  padding: 12px 14px;
  cursor: pointer;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.85)' : '#374151'};

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.04)'};
  }

  ${(props) =>
    props.isSelected &&
    `
    background: ${
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250, 70, 22, 0.1)'
    };
    color: ${props.theme?.colors?.primary || '#fa4616'};
  `}

  ${(props) =>
    props.isHighlighted &&
    `
    background: ${
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.04)'
    };
  `}
`;

export const CreateOption = styled.div`
  padding: 12px 14px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.15s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme?.colors?.textGray?.dark || '#e2e8f0'
      : props.theme?.colors?.primary || '#fa4616'};
  font-weight: 500;
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};

  &:hover {
    background: ${(props) =>
      props.disabled
        ? 'transparent'
        : props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.04)'};
  }
`;

export const CreateIcon = styled.span`
  margin-right: 8px;
  font-size: 16px;
`;

export const NoResults = styled.div`
  padding: 16px 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#64748b' : '#9CA3AF')};
  text-align: center;
  font-size: 14px;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  border-top-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const HelperText = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${(props) => (props.error ? '#ef4444' : '#64748B')};
`;

export const CheckIcon = styled.span`
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  display: flex;
  align-items: center;
`;
